import { type ReactNode } from 'react';
import { type CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

import { type BookmakersConfig } from '@cx-modules/types';

import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import { type BettingInsightsResultAttribute, type EsIndex } from '../types';

export interface BettingInsightsFilterConfiguration<
	TIndex extends EsIndex,
	TLabel = string,
> {
	attribute: BettingInsightsResultAttribute<TIndex>;
	getLabel: (this: BettingInsightsFilter<TIndex, TLabel>) => TLabel;
	render: (
		this: BettingInsightsFilter<TIndex, TLabel>,
		{
			bookmakers,
			currentRefinements,
		}: {
			bookmakers: BookmakersConfig;
			currentRefinements: CurrentRefinementsConnectorParamsItem[];
		},
	) => ReactNode;
}

export class BettingInsightsFilter<TIndex extends EsIndex, TLabel = string>
	extends BettingInsightsComponent
	implements BettingInsightsFilterConfiguration<TIndex, TLabel>
{
	attribute: BettingInsightsResultAttribute<TIndex>;
	getLabel: () => TLabel;
	render: ({
		bookmakers,
		currentRefinements,
	}: {
		bookmakers: BookmakersConfig;
		currentRefinements: CurrentRefinementsConnectorParamsItem[];
	}) => ReactNode;

	constructor({
		attribute,
		getLabel,
		render,
		...requestContext
	}: BettingInsightsFilterConfiguration<TIndex, TLabel> & RequestContext) {
		super(requestContext);
		this.attribute = attribute;
		this.getLabel = getLabel.bind(this);
		this.render = render.bind(this);
	}

	static withRequestContext<TIndex extends EsIndex, TLabel = string>(
		requestContext: RequestContext,
		config: BettingInsightsFilterConfiguration<TIndex, TLabel>,
	) {
		return new this<TIndex, TLabel>({
			...requestContext,
			...config,
		});
	}
}
